import { formatEther, parseUnits } from "ethers";

/** BigInt helpers */
function mulDiv(a: bigint, b: bigint, den: bigint): bigint {
  return (a * b) / den; // floor
}
function mulDivCeil(a: bigint, b: bigint, den: bigint): bigint {
  return (a * b + den - 1n) / den; // ceil
}

/**
 * VCP (virtual constant-product) with 18-decimal tokens.
 * Supply:
 *   TOTAL = 1_000_000_000e18, RESERVED = 200_000_000e18, QMAX = 800_000_000e18
 * Calibrated reserves (unchanged):
 *   YV_WEI = 1_073_972_603e18
 *   XV_WEI ≈ 6.226027396610034 BNB (in wei)
 */
const WEI = 10n ** 18n;
const TOTAL_SUPPLY_WEI = 1_000_000_000n * WEI;
const RESERVED_WEI = 200_000_000n * WEI;
const QMAX_WEI = TOTAL_SUPPLY_WEI - RESERVED_WEI;

const YV_WEI = 1_073_972_603n * WEI;
const XV_WEI = 6_226_027_396_610_034_000n; // 6.226027396610034e18 wei

export function tokensForBNB_Fourmeme_VCP(
  bnbWei: bigint,
  soldTokensWei: bigint
): bigint {
  if (bnbWei <= 0n) return 0n;
  if (soldTokensWei < 0n || soldTokensWei > QMAX_WEI) {
    throw new Error("soldTokens out of range");
  }

  const yRemWei = YV_WEI - soldTokensWei;
  if (yRemWei <= 0n) return 0n; // fully sold

  // u0 = s / (Yv - s), Δu = bnb / Xv, u' = u0 + Δu
  const u0_num = soldTokensWei;
  const u0_den = yRemWei;

  const du_num = bnbWei;
  const du_den = XV_WEI;

  // u' = (u0_num/u0_den) + (du_num/du_den)
  const up_num = u0_num * du_den + du_num * u0_den;
  const up_den = u0_den * du_den;

  // s' = (u'/(1+u')) * Yv = Yv * up_num / (up_den + up_num)
  const denom = up_den + up_num;
  if (denom === 0n) return 0n;

  const sPrimeWei = mulDiv(YV_WEI, up_num, denom);
  let deltaWei = sPrimeWei - soldTokensWei;
  if (deltaWei < 0n) deltaWei = 0n;

  // Cap by remaining curve supply
  const remainingWei = QMAX_WEI - soldTokensWei;
  if (deltaWei > remainingWei) deltaWei = remainingWei;

  return deltaWei; // floor
}

export function bnbForTokens_Fourmeme_VCP(
  tokensWei: bigint, // desired Δs (18dp)
  soldTokensWei: bigint
): bigint {
  if (tokensWei <= 0n) return 0n;
  if (soldTokensWei < 0n || soldTokensWei > QMAX_WEI) {
    throw new Error("soldTokens out of range");
  }

  // s1 = s0 + Δs; require s1 < Yv (asymptote)
  const s0 = soldTokensWei;
  const s1 = s0 + tokensWei;

  // Respect remaining capacity of the CURVE (not just QMAX)
  const yRem0 = YV_WEI - s0;
  if (yRem0 <= 0n) return 0n;
  if (s1 >= YV_WEI) return 0n; // can't reach the asymptote

  // x = Xv * (u(s1) - u(s0))
  //   = Xv * Yv * (s1 - s0) / ((Yv - s1)(Yv - s0))
  const deltaS = tokensWei;
  const denom = (YV_WEI - s1) * (YV_WEI - s0); // (Yv - s1)(Yv - s0)
  const num = XV_WEI * YV_WEI; // Xv * Yv

  // Ceil so the buyer never underpays due to integer truncation
  return mulDivCeil(num, deltaS, denom);
}

/**
 * Calculate tokens + BNB spent for dev & wallet buys on Fourmeme VCP curve.
 *
 * @param devBuyPercent - Developer buy percentage (e.g. 5 for 5%)
 * @param walletBuyPercent - Wallet buy percentage (e.g. 10 for 10%)
 * @returns Object with token and BNB amounts for dev, wallet, and totals.
 */
export function calculateBuys_Fourmeme(
  devBuyPercent: number,
  walletBuyPercent: number
) {
  const totalSupply = 1_000_000_000n;
  const devTokens = (BigInt(devBuyPercent) * totalSupply) / 100n;
  const walletTokens = (BigInt(walletBuyPercent) * totalSupply) / 100n;

  let sold = 0n;

  // Dev buy first
  const devBnb = bnbForTokens_Fourmeme_VCP(devTokens, sold);
  sold += devTokens;

  // Wallet buy second (after dev already bought)
  const walletBnb = bnbForTokens_Fourmeme_VCP(walletTokens, sold);
  sold += walletTokens;

  const totalBnb = devBnb + walletBnb;
  const totalTokens = devTokens + walletTokens;

  return {
    dev: {
      tokens: devTokens.toString(),
      bnbWei: devBnb,
      bnb: formatEther(devBnb),
    },
    wallet: {
      tokens: walletTokens.toString(),
      bnbWei: walletBnb,
      bnb: formatEther(walletBnb),
    },
    total: {
      tokens: totalTokens.toString(),
      bnbWei: totalBnb,
      bnb: formatEther(totalBnb),
    },
  };
}
